import { useCallback, useMemo } from "react";
import { useQueryParams } from "./use-query-params";

/**
 * Hook for managing filter state via URL query parameters.
 * Enables filter persistence across navigation and shareable filter states.
 *
 * @param defaults - Default values for all filter fields
 * @returns [filters, setFilters, resetFilters] - Current state, partial updater, and reset function
 *
 * @example
 * ```tsx
 * interface Filters {
 *   search: string;
 *   status: 'open' | 'closed' | 'all';
 *   showRejected: boolean;
 * }
 *
 * const [filters, setFilters, resetFilters] = useUrlFilters<Filters>({
 *   search: '',
 *   status: 'open',
 *   showRejected: false,
 * });
 *
 * // Update single field
 * setFilters({ search: 'bug' });
 *
 * // Update multiple fields
 * setFilters({ status: 'closed', showRejected: true });
 *
 * // Reset to defaults
 * resetFilters();
 * ```
 */
export function useUrlFilters<T extends Record<string, unknown>>(
  defaults: T,
): [T, (updates: Partial<T>) => void, () => void] {
  const { params, setParams } = useQueryParams();

  // Parse current filters from URL, falling back to defaults
  const filters = useMemo(() => {
    console.log("[DEBUG useUrlFilters] Parsing filters from URL params:", params);

    const result = { ...defaults } as T;

    for (const key in defaults) {
      const urlValue = params[key];
      const defaultValue = defaults[key];

      if (urlValue !== undefined && urlValue !== null) {
        // Parse based on default value type
        if (typeof defaultValue === "boolean") {
          (result as Record<string, unknown>)[key] = urlValue === "true";
        } else if (typeof defaultValue === "number") {
          const parsed = Number(urlValue);
          (result as Record<string, unknown>)[key] = Number.isNaN(parsed) ? defaultValue : parsed;
        } else if (Array.isArray(defaultValue)) {
          (result as Record<string, unknown>)[key] = urlValue ? urlValue.split(",") : [];
        } else {
          (result as Record<string, unknown>)[key] = urlValue;
        }
      }
    }

    console.log("[DEBUG useUrlFilters] Parsed filters result:", result);
    return result;
  }, [params, defaults]);

  // Partial update function - only updates specified fields
  const setFilters = useCallback(
    (updates: Partial<T>) => {
      console.log("[DEBUG useUrlFilters] setFilters called with:", updates);

      const params: Record<string, string | undefined> = {};

      for (const key in updates) {
        const value = updates[key];
        const defaultValue = defaults[key];

        // Handle array values
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params[key] = value.join(",");
          } else {
            params[key] = undefined;
          }
        }
        // Only set param if different from default (for cleaner URLs)
        else if (value !== defaultValue && value !== "" && value !== null && value !== undefined) {
          params[key] = String(value);
        } else {
          // Remove param if it's the default value
          params[key] = undefined;
        }
      }

      console.log("[DEBUG useUrlFilters] Setting URL params:", params);
      setParams(params);
    },
    [defaults, setParams],
  );

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    const params: Record<string, string | undefined> = {};

    for (const key in defaults) {
      params[key] = undefined;
    }

    setParams(params);
  }, [defaults, setParams]);

  return [filters, setFilters, resetFilters];
}
